import { Component } from '@theme/component';
import { gsap, SplitText, syncScroller } from '@theme/ark-gsap';

/**
 * ARK homepage motion: one module, one component per section.
 *
 * Every component builds its animations inside `gsap.matchMedia()`, so reduced-motion
 * users get no timelines, breakpoint changes rebuild cleanly, and `disconnectedCallback`
 * reverts everything (theme editor section reloads included). Content is visible by
 * default; product pieces are never hidden by an animation, they only move.
 */

const QUERIES = {
  motion: '(prefers-reduced-motion: no-preference)',
  desktop: '(min-width: 990px)',
  mobile: '(max-width: 989px)',
};

const clampUnit = gsap.utils.clamp(-1, 1);
const phone = matchMedia('(max-width: 749px)');

/**
 * @template {Record<string, any>} [R=Record<string, any>]
 * @extends {Component<R>}
 */
class ArkMotion extends Component {
  /** @type {any} */
  #mm = null;

  connectedCallback() {
    super.connectedCallback();
    this.#mm = gsap.matchMedia();
    this.#mm.add(
      QUERIES,
      (/** @type {any} */ context) => {
        syncScroller();
        if (!context.conditions.motion) {
          this.rest();
          return;
        }
        return this.animate(context.conditions, context);
      },
      this
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mm?.revert();
    this.#mm = null;
  }

  /**
   * Builds the section's animations inside a matchMedia context; wrap asynchronous work
   * in `context.add()` so it is reverted with the rest.
   * @param {{ desktop: boolean, mobile: boolean }} _conditions
   * @param {any} _context
   * @returns {void | (() => void)}
   */
  animate(_conditions, _context) {}

  /** Final, static state (reduced motion). */
  rest() {}
}

/**
 * A particle field on a canvas: soft snow or thin wind streaks. Cheap by design: one
 * pre-rendered sprite, DPR capped, paused off-screen and in background tabs. `gust`
 * (set from scroll velocity) pushes the particles for a moment.
 */
class ParticleField {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ kind: 'snow' | 'streak', density: number, minSize: number, maxSize: number, speed: number, opacity: number }} options
   */
  constructor(canvas, options) {
    this.canvas = canvas;
    this.options = options;
    this.ctx = canvas.getContext('2d');
    /** @type {{ x: number, y: number, size: number, drift: number, pace: number, alpha: number }[]} */
    this.particles = [];
    this.gust = 0;
    this.running = false;
    this.visible = true;
    this.frame = 0;
    this.last = 0;
    this.width = 0;
    this.height = 0;
    this.sprite = this.#makeSprite();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = Boolean(entry?.isIntersecting);
      this.#sync();
    });
    this.intersection.observe(canvas);
    this.onVisibility = () => this.#sync();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.resize();
  }

  #makeSprite() {
    const size = 64;
    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = size;
    const ctx = sprite.getContext('2d');
    if (!ctx) return sprite;
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(240, 245, 249, 1)');
    gradient.addColorStop(0.45, 'rgba(240, 245, 249, 0.55)');
    gradient.addColorStop(1, 'rgba(240, 245, 249, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    return sprite;
  }

  resize() {
    const { canvas } = this;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (!width || !height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.width = width;
    this.height = height;
    const count = Math.round(((width * height) / 100000) * this.options.density);
    const { minSize, maxSize } = this.options;
    this.particles = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      size: minSize + Math.random() ** 2 * (maxSize - minSize),
      drift: Math.random() * Math.PI * 2,
      pace: 0.6 + Math.random() * 0.8,
      alpha: 0.35 + Math.random() * 0.65,
    }));
  }

  start() {
    this.running = true;
    this.#sync();
  }

  #sync() {
    const shouldRun = this.running && this.visible && document.visibilityState === 'visible';
    if (shouldRun && !this.frame) {
      this.last = performance.now();
      this.frame = requestAnimationFrame(this.#tick);
    } else if (!shouldRun && this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  #tick = (/** @type {number} */ now) => {
    this.frame = requestAnimationFrame(this.#tick);
    const dt = Math.min((now - this.last) / 16.67, 3);
    this.last = now;
    const { ctx, width, height, sprite } = this;
    if (!ctx) return;
    const { speed, opacity, kind } = this.options;
    this.gust *= 0.94;
    ctx.clearRect(0, 0, width, height);

    if (kind === 'streak') {
      // Wind: thin streaks crossing left to right, slightly falling, faster in gusts.
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgb(236, 242, 247)';
      for (const p of this.particles) {
        const run = (p.pace * speed + Math.abs(this.gust) * p.pace * 2.2) * dt;
        p.drift += 0.02 * dt;
        p.x += run;
        p.y += run * 0.07 + Math.sin(p.drift) * 0.15;
        if (p.x - p.size * 8 > width) {
          p.x = -p.size * 8;
          p.y = Math.random() * height;
        }
        if (p.y > height) p.y = 0;
        const length = p.size * (8 + Math.abs(this.gust) * 4);
        ctx.globalAlpha = p.alpha * opacity;
        ctx.lineWidth = Math.max(0.6, p.size * 0.18);
        ctx.beginPath();
        ctx.moveTo(p.x - length, p.y - length * 0.07);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      return;
    }

    for (const p of this.particles) {
      p.drift += 0.012 * dt;
      p.y += (p.pace * speed + this.gust * p.pace) * dt;
      p.x += (Math.sin(p.drift) * 0.35 + 0.25) * speed * dt;
      if (p.y > height + p.size) {
        p.y = -p.size;
        p.x = Math.random() * width;
      } else if (p.y < -p.size * 2) {
        p.y = height + p.size;
      }
      if (p.x > width + p.size) p.x = -p.size;
      ctx.globalAlpha = p.alpha * opacity;
      ctx.drawImage(sprite, p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
  };

  destroy() {
    this.running = false;
    this.#sync();
    this.resizeObserver.disconnect();
    this.intersection.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}

/**
 * Reveals elements as they enter the viewport. Uses a native IntersectionObserver so the
 * trigger never depends on GSAP's ticker. Returns a cleanup function.
 * @param {HTMLElement[]} elements
 * @param {gsap.TweenVars} from - start state (never hide product pieces with it)
 * @param {gsap.TweenVars} [to] - extra tween vars
 * @param {(element: HTMLElement) => Element} [watch] - element to observe for each target.
 *   Text that starts outside its own `overflow: clip` mask is never "intersecting", so masked
 *   reveals must watch the mask instead.
 */
const revealOnEnter = (elements, from, to = {}, watch = (element) => element) => {
  if (!elements.length) return () => {};
  gsap.set(elements, from);
  /** @type {HTMLElement[]} */
  let queue = [];
  const rest = { autoAlpha: 1, scale: 1, clipPath: 'inset(0% 0% 0% 0%)' };
  const reset = Object.fromEntries(
    Object.keys(from).map((key) => [key, key in rest ? rest[/** @type {keyof typeof rest} */ (key)] : 0])
  );
  const flush = () => {
    const batch = queue;
    queue = [];
    const vars = { ...reset, duration: 1.1, ease: 'expo.out', stagger: 0.08, overwrite: true, ...to };
    // Phones scroll faster through shorter sections: keep reveals brief there.
    if (phone.matches) vars.duration = Number(vars.duration) * 0.7;
    gsap.to(batch, vars);
  };
  /** @type {Map<Element, HTMLElement>} */
  const targets = new Map(elements.map((element) => [watch(element), element]));
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        const target = targets.get(entry.target);
        if (!target) continue;
        if (!queue.length) queueMicrotask(flush);
        queue.push(target);
      }
    },
    { rootMargin: '0px 0px -6% 0px' }
  );
  targets.forEach((_, watched) => observer.observe(watched));
  return () => observer.disconnect();
};

/**
 * Soft light that follows the pointer across every ARK piece in a section.
 * @param {HTMLElement} root
 */
const pointerLight = (root) => {
  const controller = new AbortController();
  root.addEventListener(
    'pointermove',
    (event) => {
      const stage = event.target instanceof Element ? event.target.closest('.ark-piece__stage') : null;
      if (!(stage instanceof HTMLElement)) return;
      const rect = stage.getBoundingClientRect();
      stage.style.setProperty('--ark-piece-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
      stage.style.setProperty('--ark-piece-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    },
    { signal: controller.signal, passive: true }
  );
  return () => controller.abort();
};

/**
 * Reveals product pieces without ever hiding them: the card rises, its packshot settles.
 * @param {HTMLElement} root
 */
const revealPieces = (root) => {
  const pieces = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('.ark-piece')]);
  const images = /** @type {HTMLElement[]} */ (pieces.map((piece) => piece.querySelector('.ark-piece__image')).filter(Boolean));
  const stopPieces = revealOnEnter(pieces, { y: 64 }, { duration: 1.2, clearProps: 'transform' });
  // Hand the image back to the CSS hover zoom once it has settled.
  const stopImages = revealOnEnter(images, { scale: 1.12 }, { duration: 1.6, clearProps: 'transform' });
  return () => {
    stopPieces();
    stopImages();
  };
};

/** Waits for web fonts, but never longer than `max` ms. */
const fontsReady = (max = 900) =>
  Promise.race([document.fonts?.ready ?? Promise.resolve(), new Promise((resolve) => setTimeout(resolve, max))]);

/**
 * Hero: sky, wordmark and ridge planes, two snow planes, intro and scroll depth.
 *
 * @typedef {object} SummitRefs
 * @property {HTMLElement} sky
 * @property {HTMLElement} ridge
 * @property {HTMLElement} mark
 * @property {HTMLElement[]} letters
 * @property {HTMLCanvasElement} snowFar
 * @property {HTMLCanvasElement} snowNear
 * @property {HTMLElement} copy
 * @property {HTMLElement} title
 * @property {HTMLElement} [text]
 * @property {HTMLElement} [actions]
 * @property {HTMLElement} [caption]
 *
 * @extends {ArkMotion<SummitRefs>}
 */
class ArkSummit extends ArkMotion {
  requiredRefs = ['sky', 'ridge', 'mark', 'snowFar', 'snowNear', 'copy', 'title'];

  /**
   * @param {{ desktop: boolean }} conditions
   * @param {any} context
   */
  animate({ desktop }, context) {
    const { sky, ridge, mark, letters = [], snowFar, snowNear, copy, title, text, actions, caption } = this.refs;
    const followers = [text, actions, caption].filter(Boolean);
    const snow = [
      new ParticleField(snowFar, { kind: 'snow', density: desktop ? 9 : 7, minSize: 2, maxSize: 6, speed: 0.55, opacity: 0.7 }),
      new ParticleField(snowNear, { kind: 'snow', density: desktop ? 0.9 : 0.7, minSize: 10, maxSize: 26, speed: 1.6, opacity: 0.55 }),
    ];
    snow.forEach((s) => s.start());

    /** @type {any} */
    let split = null;

    if (this.dataset.intro === 'pending') {
      // Take over the CSS start states inline (GSAP would otherwise read the CSS
      // translateY(115%) as pixels), then hand control to the timeline.
      gsap.set(letters, { y: 0, yPercent: 115 });
      gsap.set([copy, ...followers], { autoAlpha: 0 });
      this.dataset.intro = 'running';

      const intro = gsap.timeline({
        defaults: { ease: 'expo.out' },
        onComplete: () => {
          this.dataset.intro = 'done';
          split?.revert();
        },
      });

      // Dawn: the frame settles in from a darker, wider shot while the ridge lifts into place.
      intro
        .fromTo(sky, { scale: 1.16, filter: 'brightness(0.4) saturate(0.7)' }, { scale: 1, filter: 'brightness(1) saturate(1)', duration: 2.6 }, 0)
        .fromTo(ridge, { y: 60 }, { y: 0, duration: 2.4 }, 0)
        // The wordmark rises from behind the mountain, letter by letter.
        .to(letters, { yPercent: 0, duration: 1.7, stagger: 0.11 }, 0.25)
        // The promise and the actions never wait for the full scene, least of all on phones.
        .set(copy, { autoAlpha: 1 }, desktop ? 0.9 : 0.45)
        .fromTo(followers, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.07 }, desktop ? 1.1 : 0.55);

      fontsReady().then(
        context.add(() => {
          if (!this.isConnected || this.dataset.intro === 'done') return;
          split = SplitText.create(title, { type: 'lines', mask: 'lines', linesClass: 'ark-line' });
          intro.fromTo(split.lines, { yPercent: 105 }, { yPercent: 0, duration: 1, stagger: 0.07 }, Math.max(intro.time(), desktop ? 0.95 : 0.45));
        })
      );
    } else {
      this.dataset.intro = 'done';
    }

    // Depth on scroll: sky slowest, the wordmark sinks back behind the ridge, the ridge rises.
    gsap
      .timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: this,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6,
          onUpdate: (self) => {
            const gust = clampUnit(self.getVelocity() / 3000) * 6;
            snow.forEach((s) => (s.gust = gust));
          },
        },
      })
      .to(sky, { yPercent: 12 }, 0)
      .to(mark, { yPercent: 75 }, 0)
      .to(ridge, { yPercent: -7 }, 0)
      .to(copy, { yPercent: -18, autoAlpha: 0.15, duration: 0.6 }, 0);

    return () => snow.forEach((s) => s.destroy());
  }

  rest() {
    this.dataset.intro = 'done';
  }
}

/**
 * Collection: pieces rise into place, the columns drift at slightly different depths,
 * and the light follows the pointer.
 *
 * @typedef {object} CollectionRefs
 * @property {HTMLElement} title
 * @property {HTMLElement} grid
 *
 * @extends {ArkMotion<CollectionRefs>}
 */
class ArkCollection extends ArkMotion {
  /** @param {{ desktop: boolean }} conditions */
  animate({ desktop }) {
    const { title, grid } = this.refs;
    const stopTitle = title ? revealOnEnter([title], { yPercent: 40, autoAlpha: 0 }, { duration: 1.2 }) : () => {};
    const stopPieces = revealPieces(this);
    const stopLight = pointerLight(this);

    if (desktop && grid) {
      // Columns travel at different depths; the high ones move a little more.
      const depth = [-48, -16, -40, -8];
      [...grid.children].forEach((item, index) => {
        gsap.to(item, {
          y: depth[index % depth.length],
          ease: 'none',
          scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
        });
      });
    }

    return () => {
      stopTitle();
      stopPieces();
      stopLight();
    };
  }
}

/**
 * Wind chapter: the photo opens from a framed window to full bleed, wind streaks cross it
 * (faster when you scroll faster), the word leans into the wind, and the feature piece
 * rises through the scene a touch faster than the page.
 *
 * @typedef {object} ChapterRefs
 * @property {HTMLElement} window
 * @property {HTMLElement} [image]
 * @property {HTMLCanvasElement} [gusts]
 * @property {HTMLElement} [word]
 * @property {HTMLElement} [copy]
 * @property {HTMLElement} [feature]
 *
 * @extends {ArkMotion<ChapterRefs>}
 */
class ArkChapter extends ArkMotion {
  /** @param {{ desktop: boolean }} conditions */
  animate({ desktop }) {
    const { window: frame, image, gusts, word, copy, feature } = this.refs;

    const field = gusts
      ? new ParticleField(gusts, { kind: 'streak', density: desktop ? 0.9 : 0.6, minSize: 3, maxSize: 9, speed: 5, opacity: 0.45 })
      : null;
    field?.start();

    // The word leans with the scroll speed, then rights itself.
    const skew = word ? gsap.quickTo(word, 'skewX', { duration: 0.6, ease: 'power3.out' }) : null;

    gsap
      .timeline({
        defaults: { ease: 'none' },
        scrollTrigger: { trigger: this, start: 'top bottom', end: 'top top', scrub: 0.8 },
      })
      .fromTo(frame, { clipPath: desktop ? 'inset(12% 10% 12% 10%)' : 'inset(8% 5% 8% 5%)' }, { clipPath: 'inset(0% 0% 0% 0%)' }, 0)
      .fromTo(image ?? frame, { scale: 1.28 }, { scale: 1.06 }, 0)
      .fromTo(word ?? [], { xPercent: -14 }, { xPercent: 0 }, 0);

    gsap.to(image ?? frame, {
      yPercent: 7,
      ease: 'none',
      scrollTrigger: { trigger: this, start: 'top top', end: 'bottom top', scrub: 0.8 },
    });

    gsap.to(
      {},
      {
        scrollTrigger: {
          trigger: this,
          start: 'top bottom',
          end: 'bottom top',
          onUpdate: (self) => {
            const velocity = clampUnit(self.getVelocity() / 2500);
            skew?.(velocity * (desktop ? -8 : -4));
            if (field) field.gust = velocity * 6;
          },
        },
      }
    );

    if (feature) {
      gsap.fromTo(
        feature,
        { yPercent: 18 },
        {
          yPercent: -10,
          ease: 'none',
          scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
        }
      );
    }

    const stopCopy = copy ? revealOnEnter([copy], { y: 40, autoAlpha: 0 }, { duration: 1 }) : () => {};
    const stopLight = pointerLight(this);

    return () => {
      field?.destroy();
      stopCopy();
      stopLight();
    };
  }
}

/**
 * Deep cold: ice-filled letters rise out of their masks, the ice drifts inside them,
 * and the feature piece stands in front at its own depth.
 *
 * @typedef {object} FrostRefs
 * @property {HTMLElement[]} [lines]
 * @property {HTMLElement} [copy]
 * @property {HTMLElement} [feature]
 *
 * @extends {ArkMotion<FrostRefs>}
 */
class ArkFrost extends ArkMotion {
  animate() {
    const { lines = [], copy, feature } = this.refs;

    const stopLines = revealOnEnter(
      lines,
      { yPercent: 105 },
      { duration: 1.4, stagger: 0.12 },
      (line) => line.parentElement ?? line
    );

    lines.forEach((line, index) => {
      gsap.fromTo(
        line,
        { '--ark-frost-shift': index % 2 ? '85%' : '10%' },
        {
          '--ark-frost-shift': index % 2 ? '30%' : '65%',
          ease: 'none',
          scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
        }
      );
    });

    if (feature) {
      gsap.fromTo(
        feature,
        { yPercent: 22 },
        {
          yPercent: -12,
          ease: 'none',
          scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
        }
      );
    }

    const stopCopy = copy ? revealOnEnter([copy], { y: 40, autoAlpha: 0 }, { duration: 1 }) : () => {};
    const stopPieces = revealPieces(this);
    const stopLight = pointerLight(this);

    return () => {
      stopLines();
      stopCopy();
      stopPieces();
      stopLight();
    };
  }
}

/**
 * Immersive scene. Whiteout: the fog thickens as you enter and thins as you leave.
 * Street: snow falls over the city, the blade sign slides against the photo.
 *
 * @typedef {object} SceneRefs
 * @property {HTMLElement} frame
 * @property {HTMLElement} [image]
 * @property {HTMLElement} [fog]
 * @property {HTMLCanvasElement} [snowFar]
 * @property {HTMLCanvasElement} [snowNear]
 * @property {HTMLElement} [title]
 * @property {HTMLElement} [copy]
 *
 * @extends {ArkMotion<SceneRefs>}
 */
class ArkScene extends ArkMotion {
  /** @param {{ desktop: boolean }} conditions */
  animate({ desktop }) {
    const { frame, image, fog, snowFar, snowNear, title, copy } = this.refs;
    const street = this.dataset.style === 'street';

    gsap.fromTo(
      image ?? frame,
      { scale: 1.18 },
      {
        scale: 1,
        ease: 'none',
        scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
      }
    );

    /** @type {ParticleField[]} */
    const fields = [];

    if (street) {
      if (snowFar) fields.push(new ParticleField(snowFar, { kind: 'snow', density: desktop ? 7 : 5, minSize: 2, maxSize: 5, speed: 0.8, opacity: 0.7 }));
      if (snowNear) fields.push(new ParticleField(snowNear, { kind: 'snow', density: desktop ? 0.8 : 0.6, minSize: 9, maxSize: 22, speed: 2, opacity: 0.5 }));
      fields.forEach((f) => f.start());

      if (title) {
        gsap.fromTo(
          title,
          { yPercent: 14 },
          {
            yPercent: -10,
            ease: 'none',
            scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
          }
        );
      }
    } else if (fog) {
      // Entering the whiteout: the fog builds to the middle of the scene, then clears.
      gsap
        .timeline({
          defaults: { ease: 'none' },
          scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 },
        })
        .fromTo(fog, { opacity: 0.1 }, { opacity: 0.9, duration: 0.5 })
        .to(fog, { opacity: 0.3, duration: 0.5 });
    }

    const stopTitle = !street && title ? revealOnEnter([title], { yPercent: 30, autoAlpha: 0 }, { duration: 1.4 }) : () => {};
    const stopCopy = copy ? revealOnEnter([copy], { y: 32, autoAlpha: 0 }, { duration: 1 }) : () => {};

    return () => {
      fields.forEach((f) => f.destroy());
      stopTitle();
      stopCopy();
    };
  }
}

/**
 * Manifesto: words light up in reading order while the statement crosses the viewport.
 *
 * @typedef {object} ManifestoRefs
 * @property {HTMLElement} text
 *
 * @extends {ArkMotion<ManifestoRefs>}
 */
class ArkManifesto extends ArkMotion {
  animate() {
    const { text } = this.refs;
    if (!text) return;

    SplitText.create(text, {
      type: 'words',
      autoSplit: true,
      onSplit: (/** @type {any} */ self) =>
        gsap.fromTo(
          self.words,
          { opacity: 0.14 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.12,
            scrollTrigger: { trigger: text, start: 'top 82%', end: 'bottom 48%', scrub: true },
          }
        ),
    });
  }
}

/**
 * Finale: the statement's words light up in reading order while the forest closes in.
 *
 * @typedef {object} FinaleRefs
 * @property {HTMLElement} media
 * @property {HTMLElement} [image]
 * @property {HTMLElement} [statement]
 *
 * @extends {ArkMotion<FinaleRefs>}
 */
class ArkFinale extends ArkMotion {
  /** @param {{ desktop: boolean }} _conditions */
  animate(_conditions) {
    const { image, shade, statement, actions } = this.refs;
    const lines = /** @type {HTMLElement[]} */ (this.refs.lines ?? []);

    // A short held scene: the photo settles and darkens while the words arrive in order.
    this.setAttribute('data-scene', '');
    const timeline = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: { trigger: this, start: 'top 65%', end: 'bottom bottom', scrub: 0.6 },
    });
    if (image) timeline.fromTo(image, { scale: 1.16 }, { scale: 1, duration: 1 }, 0);
    if (shade) timeline.fromTo(shade, { opacity: 0.55 }, { opacity: 1, duration: 1 }, 0);
    lines.forEach((line, index) => {
      timeline.from(line, { yPercent: 105, duration: 0.3, ease: 'power2.out' }, 0.05 + index * 0.16);
    });
    if (statement) timeline.from(statement, { autoAlpha: 0, y: 24, duration: 0.22 }, 0.45);
    if (actions) timeline.from(actions, { autoAlpha: 0, y: 16, duration: 0.2 }, 0.62);

    return () => this.removeAttribute('data-scene');
  }
}

/**
 * Essentials: the compact product rail rises in and shares the pointer light.
 *
 * @extends {ArkMotion}
 */
class ArkEssentials extends ArkMotion {
  animate() {
    const title = /** @type {HTMLElement | null} */ (this.querySelector('.ark-essentials__title'));
    const stopTitle = title ? revealOnEnter([title], { yPercent: 30, autoAlpha: 0 }, { duration: 1 }) : () => {};
    const stopPieces = revealPieces(this);
    const stopLight = pointerLight(this);
    return () => {
      stopTitle();
      stopPieces();
      stopLight();
    };
  }
}

/**
 * Product story: the weather photo settles as it scrolls in and the "Built for" line
 * lights up word by word as it is read.
 *
 * @extends {ArkMotion}
 */
class ArkProductStory extends ArkMotion {
  animate() {
    const { image, statement } = this.refs;
    const band = this.querySelector('.ark-story__band');
    if (!band) return;

    if (image) {
      gsap.fromTo(
        image,
        { scale: 1.18 },
        { scale: 1, ease: 'none', scrollTrigger: { trigger: band, start: 'top bottom', end: 'bottom center', scrub: 0.8 } }
      );
    }

    if (statement) {
      SplitText.create(statement, {
        type: 'words',
        autoSplit: true,
        onSplit: (/** @type {any} */ self) =>
          gsap.fromTo(
            self.words,
            { opacity: 0.25 },
            {
              opacity: 1,
              ease: 'none',
              stagger: 0.1,
              scrollTrigger: { trigger: statement, start: 'top 88%', end: 'bottom 60%', scrub: true },
            }
          ),
      });
    }
  }
}

/**
 * Recommendations: fetched from Shopify's recommendation endpoint after first paint.
 * Complementary products first; when the store has none set up, related products.
 * Shopping UI, so no entrance animation: the cards appear as soon as they are ready.
 *
 * @extends {Component}
 */
class ArkRecommendations extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#light();
    if (this.refs.results?.dataset.source === 'shopify') return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    io.observe(this);
    this.#controller.signal.addEventListener('abort', () => io.disconnect());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  async #load() {
    const intent = this.dataset.intent || 'related';
    const intents = intent === 'complementary' ? ['complementary', 'related'] : [intent];
    for (const name of intents) {
      try {
        const response = await fetch(`${this.dataset.url}&intent=${name}`, { signal: this.#controller.signal });
        if (!response.ok) continue;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = html.querySelector('[ref="results"]');
        if (!results || Number(results.getAttribute('data-count')) === 0) continue;
        this.refs.results?.replaceWith(results);
        return;
      } catch (error) {
        if (/** @type {Error} */ (error).name === 'AbortError') return;
      }
    }
  }

  #light() {
    const stop = pointerLight(this);
    this.#controller.signal.addEventListener('abort', stop);
  }
}

const define = (/** @type {string} */ name, /** @type {CustomElementConstructor} */ ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

define('ark-summit', ArkSummit);
define('ark-collection', ArkCollection);
define('ark-chapter', ArkChapter);
define('ark-frost', ArkFrost);
define('ark-scene', ArkScene);
define('ark-manifesto', ArkManifesto);
define('ark-finale', ArkFinale);
define('ark-essentials', ArkEssentials);
define('ark-product-story', ArkProductStory);
define('ark-recommendations', ArkRecommendations);
